/**
 * useWebhookEvents
 *
 * Subscribes to the backend SSE stream at /api/dashboard/live-events and calls
 * `onEvent` whenever a Meraki webhook is processed (alert created or auto-resolved).
 *
 * Uses the shared token cache from noc-api-client so it doesn't fire extra
 * round-trips to /api/auth/session on every reconnect.
 */
'use client'

import { useEffect, useRef } from 'react'
import { acquireToken } from '@/lib/noc-api-client'

export interface WebhookEvent {
  type: 'alert_created' | 'alert_auto_resolved'
  event_id: number
  resolved_event_id?: number
  alert_type: string
  priority?: string
  device_name: string
  network_name: string
  down_at?: string
  up_at?: string
  downtime?: string
}

type EventHandler = (event: WebhookEvent) => void

const getNocApiUrl = (): string => {
  if (process.env.NEXT_PUBLIC_NOC_API_URL) return process.env.NEXT_PUBLIC_NOC_API_URL
  if (typeof window !== 'undefined') return window.location.origin
  return 'http://localhost:8001'
}

const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

export function useWebhookEvents(onEvent: EventHandler) {
  const esRef = useRef<EventSource | null>(null)
  const retryDelayRef = useRef<number>(1000)
  const unmountedRef = useRef<boolean>(false)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    unmountedRef.current = false

    async function connect() {
      if (unmountedRef.current) return

      let token: string | null = null
      try {
        token = await acquireToken()
      } catch {
        scheduleReconnect()
        return
      }

      if (!token) {
        scheduleReconnect()
        return
      }

      const url = `${getNocApiUrl()}${BASE_PATH}/api/dashboard/live-events?token=${encodeURIComponent(token)}`

      const es = new EventSource(url)
      esRef.current = es

      es.onopen = () => {
        retryDelayRef.current = 1000
      }

      es.onmessage = (evt) => {
        if (!evt.data || evt.data.startsWith(':')) return
        try {
          const parsed: WebhookEvent = JSON.parse(evt.data)
          if (parsed.type === 'alert_created' || parsed.type === 'alert_auto_resolved') {
            onEvent(parsed)
          }
        } catch {
          // Ignore malformed frames
        }
      }

      es.onerror = () => {
        es.close()
        esRef.current = null
        if (!unmountedRef.current) {
          scheduleReconnect()
        }
      }
    }

    function scheduleReconnect() {
      if (unmountedRef.current) return
      retryTimerRef.current = setTimeout(() => {
        retryDelayRef.current = Math.min(retryDelayRef.current * 2, 30000)
        connect()
      }, retryDelayRef.current)
    }

    connect()

    return () => {
      unmountedRef.current = true
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
